// The top-level class to hold all functionality together.
import React, { Suspense } from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { initializeApp as firebaseInitApp } from 'firebase/app';
import {
  getMessaging as firebaseGetMessaging,
  getToken as firebaseGetToken,
  deleteToken as firebaseDelToken,
  onMessage as firebaseOnMessage,
} from 'firebase/messaging';

import { Drafty, Tinode } from 'tinode-sdk';

import Alert from '@widgets/alert.jsx';
import ContextMenu from '@widgets/context-menu.jsx';
import ErrorPanel from '@widgets/error-panel.jsx';
import CallIncoming from '@widgets/call-incoming.jsx';
import Header from './header/header.jsx';

const InfoView = React.lazy(_ => import('@views/info-view.jsx'));
const MessagesView = React.lazy(_ => import('@views/messages-view.jsx'));
import SidepanelView from '@views/sidepanel-view.jsx';

import {
  API_KEY,
  APP_NAME,
  GUEST_LOGIN,
  GUEST_PASSWORD,
  SSO_AUTH_SCHEME,
  DEFAULT_COLOR_SCHEME,
  DEFAULT_P2P_ACCESS_MODE,
  DEFAULT_TEXT_SIZE,
  FORCED_TRANSPORT,
  LOGGING_ENABLED,
  MEDIA_BREAKPOINT,
  WAKE_UP_TICK,
  WAKE_UP_TIMEOUT,
} from '../config.js';
import {
  CALL_STATE_NONE,
  CALL_STATE_OUTGOING_INITATED,
  CALL_STATE_INCOMING_RECEIVED,
  CALL_STATE_IN_PROGRESS,
  CALL_HEAD_STARTED,
} from '../constants.js';
import { PACKAGE_VERSION } from '../version.js';
import authClient, { SSO_SESSION_STATUS } from '@lib/auth-client.js';
import BFCacheGuard from '@lib/bfcache-guard.js';
import { makeImageUrl } from '@lib/blob-helpers.js';
import { detectServerAddress, isLocalHost, isSecureConnection } from '@lib/host-name.js';
import LocalStorageUtil from '@lib/local-storage.js';
import HashNavigation from '@lib/navigation.js';
import { isRussianLocale } from '@lib/site-locale.js';
import { secondsToTime } from '@lib/strformat.js';
import { localizedPublicName } from '@lib/user-name.js';
import { defaultWallpaper, updateFavicon } from '@lib/utils.js';
import styles from './tinode-web.module.css';

// Sound to play on message received.
const POP_SOUND = new Audio('audio/msg.m4a');

const SSO_LOGIN_MARKER = '__sso__';
const SSO_SESSION_CHECK_INTERVAL = 15000;

const PROJECT_NAME = 'РепкаЧат'

function loginSSO(tinode, login, password, cred) {
  return tinode.login(SSO_AUTH_SCHEME, btoa(`${login}:${password}`), cred).then(ctrl => {
    tinode._login = login;
    return ctrl;
  });
}

function ssoLoginUrl() {
  const params = new URLSearchParams();
  params.set('redirectUrl', window.location.href);
  params.set('preferred', 'email');
  params.set('app', PROJECT_NAME);
  return `${window.location.origin}/auth/login?${params.toString()}`;
}

export function ssoProfileUrl() {
  const params = new URLSearchParams();
  params.set('redirectUrl', window.location.href);
  params.set('require', 'username');
  params.set('app', PROJECT_NAME);
  return `${window.location.origin}/auth/profile?${params.toString()}`;
}

function ssoLogoutUrl() {
  const params = new URLSearchParams();
  params.set('redirectUrl', window.location.origin + window.location.pathname);
  return `${window.location.origin}/auth/logout?${params.toString()}`;
}

// Маршруты, где не нужно поверх текущего экрана автоматически логинить гостя.
// В SSO-only режиме локальные login/register/reset больше не используются.
const ACCOUNT_FLOW_PANELS = ['settings'];
const ADMIN_SSO_USER_ID = 'rbs_admin';

const ACCOUNT_SETTINGS_PANELS = [
  'edit',
  'general',
  'crop',
  'acc_general',
  'notif',
  'security',
  'support',
  'wallpapers',
];
const AUTH_PANELS = ['settings'];

const messages = defineMessages({
  reconnect_countdown: {
    id: 'reconnect_countdown',
    defaultMessage: 'Disconnected. Reconnecting in {seconds}…',
    description: 'Message shown when an app update is available.',
  },
  reconnect_now: {
    id: 'reconnect_now',
    defaultMessage: 'Try now',
    description: 'Prompt for reconnecting now',
  },
  push_init_failed: {
    id: 'push_init_failed',
    defaultMessage: 'Failed to initialize push notifications',
    description: 'Error message when push notifications have failed to initialize.',
  },
  notifications_enabled: {
    id: 'notifications_enabled',
    defaultMessage: 'Notifications enabled',
    description: 'Уведомление, которое показывается после включения push-уведомлений.',
  },
  no_connection: {
    id: 'no_connection',
    defaultMessage: 'No connection',
    description: 'Warning that the user is offline.',
  },
  menu_item_info: {
    id: 'menu_item_info',
    defaultMessage: 'Info',
    description: 'Show extended topic information',
  },
  menu_item_audio_call: {
    id: 'menu_item_audio_call',
    defaultMessage: 'Call',
    description: 'Start audio call',
  },
  menu_item_video_call: {
    id: 'menu_item_video_call',
    defaultMessage: 'Video call',
    description: 'Start video call',
  },
  guest_user_name: {
    id: 'guest_user_name',
    defaultMessage: 'Guest',
    description: 'Display name of the shared guest account',
  },
});

class TinodeWeb extends React.Component {
  constructor(props) {
    super(props);

    this.selfRef = React.createRef();

    this.state = this.getBlankState();
    this.ssoSessionTicker = null;
    this.hiWaiters = [];
    this.bfcacheGuard = new BFCacheGuard();

    this.handleResize = this.handleResize.bind(this);
    this.handleHashRoute = this.handleHashRoute.bind(this);
    this.handleOnline = this.handleOnline.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleColorSchemeChange = this.handleColorSchemeChange.bind(this);
    this.checkForAppUpdate = this.checkForAppUpdate.bind(this);
    this.handleVisibilityEvent = this.handleVisibilityEvent.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleConnected = this.handleConnected.bind(this);
    this.connectAfterHi = this.connectAfterHi.bind(this);
    this.handleAutoreconnectIteration = this.handleAutoreconnectIteration.bind(this);
    this.startSilentSSOLogin = this.startSilentSSOLogin.bind(this);
    this.startGuestLogin = this.startGuestLogin.bind(this);
    this.handleLoginSuccessful = this.handleLoginSuccessful.bind(this);
    this.handleDisconnect = this.handleDisconnect.bind(this);
    this.tnMeMetaDesc = this.tnMeMetaDesc.bind(this);
    this.tnMeContactUpdate = this.tnMeContactUpdate.bind(this);
    this.tnMeSubsUpdated = this.tnMeSubsUpdated.bind(this);
    this.resetContactList = this.resetContactList.bind(this);
    this.tnInitFind = this.tnInitFind.bind(this);
    this.tnFndSubsUpdated = this.tnFndSubsUpdated.bind(this);
    this.handleSearchContacts = this.handleSearchContacts.bind(this);
    this.handleTopicSelected = this.handleTopicSelected.bind(this);
    this.handleHideMessagesView = this.handleHideMessagesView.bind(this);
    this.handleSendMessage = this.handleSendMessage.bind(this);
    this.handleNewChatInvitation = this.handleNewChatInvitation.bind(this);
    this.handleUpdateAccountTagsRequest = this.handleUpdateAccountTagsRequest.bind(this);
    this.handleToggleIncognitoMode = this.handleToggleIncognitoMode.bind(this);
    this.handleChangeColorSchema = this.handleChangeColorSchema.bind(this);
    this.handleChangeTextSize = this.handleChangeTextSize.bind(this);
    this.handleSendOnEnter = this.handleSendOnEnter.bind(this);
    this.handleSelectWallpapers = this.handleSelectWallpapers.bind(this);
    this.handleWallpaperSelected = this.handleWallpaperSelected.bind(this);
    this.handleSettings = this.handleSettings.bind(this);
    this.handleGlobalSettings = this.handleGlobalSettings.bind(this);
    this.handleShowArchive = this.handleShowArchive.bind(this);
    this.handleShowBlocked = this.handleShowBlocked.bind(this);
    this.handleToggleMessageSounds = this.handleToggleMessageSounds.bind(this);
    this.initFCMessaging = this.initFCMessaging.bind(this);
    this.registerFCMTokenWithServer = this.registerFCMTokenWithServer.bind(this);
    this.toggleFCMToken = this.toggleFCMToken.bind(this);
    this.handlePushMessage = this.handlePushMessage.bind(this);
    this.handleSidepanelCancel = this.handleSidepanelCancel.bind(this);
    this.handleStartTopicRequest = this.handleStartTopicRequest.bind(this);
    this.handleNewTopicCreated = this.handleNewTopicCreated.bind(this);
    this.handleTopicUpdateRequest = this.handleTopicUpdateRequest.bind(this);
    this.handleUnarchive = this.handleUnarchive.bind(this);
    this.handleChangePermissions = this.handleChangePermissions.bind(this);
    this.handleTagsUpdateRequest = this.handleTagsUpdateRequest.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleGuestSignInRequest = this.handleGuestSignInRequest.bind(this);
    this.handleDeleteAccount = this.handleDeleteAccount.bind(this);
    this.handleDeleteTopicRequest = this.handleDeleteTopicRequest.bind(this);
    this.handleDeleteMessagesRequest = this.handleDeleteMessagesRequest.bind(this);
    this.handleLeaveUnsubRequest = this.handleLeaveUnsubRequest.bind(this);
    this.handleBlockTopicRequest = this.handleBlockTopicRequest.bind(this);
    this.handleReportTopic = this.handleReportTopic.bind(this);
    this.handleShowContextMenu = this.handleShowContextMenu.bind(this);
    this.defaultTopicContextMenu = this.defaultTopicContextMenu.bind(this);
    this.handleHideContextMenu = this.handleHideContextMenu.bind(this);
    this.handleShowAlert = this.handleShowAlert.bind(this);
    this.handleShowInfoView = this.handleShowInfoView.bind(this);
    this.handleMemberUpdateRequest = this.handleMemberUpdateRequest.bind(this);
    this.handleContextMenuAction = this.handleContextMenuAction.bind(this);


    this.handleStartVideoCall = this.handleStartVideoCall.bind(this);
    this.handleStartAudioCall = this.handleStartAudioCall.bind(this);
    this.handleInfoMessage = this.handleInfoMessage.bind(this);
    this.handleDataMessage = this.handleDataMessage.bind(this);
    this.handleCallClose = this.handleCallClose.bind(this);

    this.handleCallInvite = this.handleCallInvite.bind(this);
    this.handleCallRinging = this.handleCallRinging.bind(this);
    this.handleCallHangup = this.handleCallHangup.bind(this);
    this.handleCallSendOffer = this.handleCallSendOffer.bind(this);
    this.handleCallIceCandidate = this.handleCallIceCandidate.bind(this);
    this.handleCallSendAnswer = this.handleCallSendAnswer.bind(this);

    this.handleCallAccept = this.handleCallAccept.bind(this);

    this.sendMessageToTopic = this.sendMessageToTopic.bind(this);

    // FIXME: this is unused.
    this.callTimeoutTimer = null;
  }

  getBlankState() {
    const settings = LocalStorageUtil.getObject('settings') || {};
    const persist = !!LocalStorageUtil.getObject('keep-logged-in');
    const parsedNav = HashNavigation.parseUrlHash(window.location.hash);
    const accountFlow = ACCOUNT_FLOW_PANELS.includes(parsedNav.path[0]);
    const authRestoring = !accountFlow;
    if (!settings.wallpaper) {
      const wallpaper = defaultWallpaper();
      settings.wallpaper = wallpaper.name || '';
      settings.wallpaperSize = wallpaper.size || 0;
      settings.wallpaperBlur = 0;
    }
    return {
      connected: false,
      // Connected and subscribed to 'me'
      ready: false,
      // Auto-login больше не означает token-login Tinode.
      // SSO/guest flow запускается явно в componentDidMount после проверки текущего route.
      autoLogin: false,
      authRestoring: authRestoring,
      // Гостевой режим нужен дочерним компонентам для read-only поведения.
      guestLogin: false,
      transport: FORCED_TRANSPORT,
      serverAddress: settings.serverAddress || detectServerAddress(),
      secureConnection: settings.secureConnection === undefined ? isSecureConnection() : settings.secureConnection,
      serverVersion: 'no connection',
      // "On" is the default, so saving the "off" state.
      messageSounds: !settings.messageSoundsOff,
      incognitoMode: false,
      // Manually selected color scheme. Default: 'system default' ('auto').
      colorSchema: settings.colorSchema || DEFAULT_COLOR_SCHEME,
      systemColorSchema: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
      // Wallpaper settings.
      wallpaper: settings.wallpaper,
      wallpaperSize: settings.wallpaperSize,
      wallpaperBlur: 0,
      // Text size in points. Default size: 10pt
      textSize: settings.textSize || DEFAULT_TEXT_SIZE,
      // По умолчанию: 'plain' (отправка по Enter).
      sendOnEnter: settings.sendOnEnter || 'plain',
      // Сохранённый выбор пользователя: включать ли уведомления.
      desktopAlerts: !!settings.desktopAlerts,
      // Доступность чекбокса зависит от браузера, HTTPS/localhost и Firebase-конфига.
      desktopAlertsEnabled:
        (isSecureConnection() || isLocalHost()) &&
        typeof firebaseInitApp != 'undefined' &&
        typeof navigator != 'undefined' &&
        typeof FIREBASE_INIT != 'undefined',
      firebaseToken: LocalStorageUtil.getObject('firebase-token'),

      applicationVisible: !document.hidden,

      errorText: '',
      errorLevel: null,
      errorAction: undefined,
      errorActionText: null,

      sidePanelSelected: authRestoring ? '' : parsedNav.path[0] || 'settings',
      sidePanelTitle: null,
      sidePanelAvatar: null,
      myTrustedBadges: [],
      // На старте показываем загрузку, если идет восстановление авторизации.
      loadSpinnerVisible: authRestoring,

      persist: persist,
      myUserId: null,
      ssoUserId: null,
      liveConnection: navigator.onLine,
      topicSelected: '',
      topicSelectedOnline: false,
      topicSelectedAcs: null,
      newTopicParams: null,
      // Блокируем ручной ввод логина, пока не ясно, восстановится ли сессия автоматически.
      loginDisabled: authRestoring,
      displayMobile: window.innerWidth <= MEDIA_BREAKPOINT,
      infoPanel: undefined,
      mobilePanel: 'sidepanel',

      // Video calls.
      callTopic: undefined,
      callState: CALL_STATE_NONE,
      callAudioOnly: undefined,
      // If true, call state should be transitioned to CALL_STATE_IN_PROGRESS upon
      // switching to the call topic.
      callShouldStart: false,

      contextMenuVisible: false,
      contextMenuBounds: null,
      contextMenuClickAt: null,
      contextMenuParams: null,
      contextMenuItems: [],

      // Modal alert dialog.
      alertVisible: false,
      alertParams: {},

      // Chats as shown in the ContactsView
      chatList: [],
      // Contacts returned by a search query.
      searchResults: [],
      // Merged results of a search query and p2p chats.
      searchableContacts: [],
      // Topic to go to after login.
      requestedTopic: undefined,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.handleOnlineOn = _ => {
      this.handleOnline(true);
    };
    window.addEventListener('online', this.handleOnlineOn);
    this.handleOnlineOff = _ => {
      this.handleOnline(false);
    };
    window.addEventListener('offline', this.handleOnlineOff);
    window.addEventListener('hashchange', this.handleHashRoute);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('pageshow', this.handlePageShow);
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.handleColorSchemeChange);

    // Window/tab visible or invisible for pausing timers.
    document.addEventListener('visibilitychange', this.handleVisibilityEvent);

    // Listen for Escape key to clear selections.
    document.addEventListener('keydown', this.handleEscapeKey);

    document.documentElement.style.colorScheme =
      this.state.colorSchema == 'auto' ? 'light dark' : this.state.colorSchema;
    document.documentElement.style.setProperty('--message-text-size', `${this.state.textSize}pt`);

    this.applyWallpaperSettings(
      this.state.wallpaper,
      this.state.wallpaperSize,
      this.state.wallpaperBlur,
      this.state.colorSchema == 'auto' ? this.state.systemColorSchema : this.state.colorSchema
    );

    // Process background notifications from the service worker.
    if (typeof BroadcastChannel == 'function') {
      const serviceWorkerChannel = new BroadcastChannel('tinode-sw');
      serviceWorkerChannel.addEventListener('message', this.handlePushMessage);
    } else {
      // Safari is broken by design.
      console.warn('Your browser does not support BroadcastChannel. Some features will not be available');
    }

    // Detect if computer just woke up from sleep. Checks how much time has passed since the last tick.
    // If too much time has passed then the computer was asleep.
    this.lastWakeUpCheck = new Date().getTime();
    this.wakeUpTicker = setInterval(_ => {
      const now = new Date().getTime();
      if (now - this.lastWakeUpCheck > WAKE_UP_TIMEOUT) {
        // Trigger reconnect.
        this.handleOnlineOn();
      }
      this.lastWakeUpCheck = now;
    }, WAKE_UP_TICK);

    this.setState({
      viewportWidth: document.documentElement.clientWidth,
      viewportHeight: document.documentElement.clientHeight,
    });

    new Promise(resolve => {
      this.tinode = TinodeWeb.tnSetup(
        this.state.serverAddress,
        isSecureConnection(),
        this.state.transport,
        this.props.intl.locale,
        this.state.persist,
        resolve
      );
      this.tinode.onConnect = this.handleConnected;
      this.tinode.onDisconnect = this.handleDisconnect;
      this.tinode.onAutoreconnectIteration = this.handleAutoreconnectIteration;
      this.tinode.onInfoMessage = this.handleInfoMessage;
      this.tinode.onDataMessage = this.handleDataMessage;
    }).then(_ => {
      // Восстанавливаем состояние интерфейса из кеша SDK. Способ авторизации ниже
      // всегда выбирается по текущей SSO-сессии, а не по сохранённому токену.
      this.resetContactList();

      const parsedNav = HashNavigation.parseUrlHash(window.location.hash);
      const accountFlow = ACCOUNT_FLOW_PANELS.includes(parsedNav.path[0]);
      const requestedTopic = Tinode.topicType(parsedNav.path[1]) ? parsedNav.path[1] : undefined;
      this.setState({ requestedTopic: requestedTopic });

      if (requestedTopic || accountFlow) {
        this.handleHashRoute();
      }

      if (accountFlow) {
        // Пользователь явно открыл служебную панель. Не логиним гостя поверх нее.
        this.setState({
          authRestoring: false,
          loginDisabled: false,
          loadSpinnerVisible: false,
        });
        return;
      }

      this.startSilentSSOLogin(requestedTopic);
    });
  }

  // Старый Tinode token не должен иметь приоритет над текущей SSO-session.
  clearTinodeAuthToken() {
    LocalStorageUtil.removeItem('auth-token');
    this.tinode.setAuthToken(null);
  }

  clearSSOIdentity() {
    this.setState({
      myUserId: null,
      ssoUserId: null,
      sidePanelTitle: null,
      sidePanelAvatar: null,
      myTrustedBadges: [],
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('hashchange', this.handleHashRoute);
    window.removeEventListener('online', this.handleOnlineOn);
    window.removeEventListener('offline', this.handleOnlineOff);
    window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this.handleColorSchemeChange);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('pageshow', this.handlePageShow);
    document.removeEventListener('visibilitychange', this.handleVisibilityEvent);
    document.removeEventListener('keydown', this.handleEscapeKey);

    clearInterval(this.wakeUpTicker);
    clearInterval(this.ssoSessionTicker);
  }

  // Setup transport (usually websocket) and server address. This will terminate connection with the server.
  static tnSetup(serverAddress, secureConnection, transport, locale, persistentCache, onSetupCompleted) {
    const tinode = new Tinode(
      {
        appName: APP_NAME,
        host: serverAddress,
        apiKey: API_KEY,
        transport: FORCED_TRANSPORT,
        secure: secureConnection,
        persist: persistentCache,
      },
      onSetupCompleted
    );
    tinode.authorizeURL = tinode.authorizeURL.bind(tinode);
    tinode.setHumanLanguage(locale);
    tinode.enableLogging(LOGGING_ENABLED, true);
    return tinode;
  }

  // Tinode получил push-уведомление от сервера.
  handlePushMessage(payload) {
    const data = payload.data || payload;
    console.info('Получено push-уведомление переднего плана:', data.title || data.what, data.topic);
    this.tinode.oobNotification(data);
    this.showForegroundPushNotificationInServiceWorker(data);
  }

  showForegroundPushNotificationInServiceWorker(data) {
    // Сообщения переднего плана от Firebase приходят в окно, но показывать уведомление должен сервисный воркер.
    if (
      !data ||
      data.title != 'notifications_enabled' ||
      typeof Notification == 'undefined' ||
      Notification.permission != 'granted' ||
      !navigator.serviceWorker
    ) {
      return;
    }

    navigator.serviceWorker.ready
      .then(reg => {
        const worker = reg.active || reg.waiting || reg.installing;
        if (!worker) {
          throw new Error('Сервисный воркер недоступен');
        }
        console.info('Передача push-уведомления переднего плана в сервисный воркер:', data.title);
        worker.postMessage({
          action: 'show-notification',
          data: {
            ...data,
            title: data.title || messages.notifications_enabled.id,
          },
        });
      })
      .catch(err => {
        console.warn('Не удалось передать push-уведомление переднего плана в сервисный воркер', err);
      });
  }

  initFCMessaging() {
    const { formatMessage, locale } = this.props.intl;
    const onError = (msg, err) => {
      console.error(msg, err);
      this.handleError(formatMessage(messages.push_init_failed), 'err');
      this.setState({ desktopAlerts: false, firebaseToken: null });
      LocalStorageUtil.updateObject('settings', { desktopAlerts: false });
    };

    try {
      this.fcm = firebaseGetMessaging(firebaseInitApp(FIREBASE_INIT, APP_NAME));
      return navigator.serviceWorker
        .getRegistration('/chat/service-worker.js')
        .then(reg => {
          if (reg) {
            // Для уже зарегистрированного service worker сначала подписываемся на события,
            // а затем явно запускаем проверку обновления.
            this.checkForAppUpdate(reg);
            return reg
              .update()
              .catch(err => {
                console.warn('Failed to check for service worker update', err);
              })
              .then(_ => reg);
          }
          // При первой регистрации updatefound может сработать до возврата промиса register(),
          // поэтому checkForAppUpdate дополнительно проверяет текущий reg.installing.
          return navigator.serviceWorker.register('/chat/service-worker.js').then(reg => {
            this.checkForAppUpdate(reg);
            return reg;
          });
        })
        .then(reg => {
          // Передаем service worker локаль и версию приложения.
          (reg.active || reg.waiting || reg.installing).postMessage(
            JSON.stringify({ locale: locale, version: PACKAGE_VERSION })
          );
          if (this.state.guestLogin) {
            // Гость не регистрирует device token на сервере, чтобы не отправлять mutating {hi}.
            return null;
          }
          // Запрашиваем токен push-уведомлений только для обычной сессии.
          return TinodeWeb.requestFCMToken(this.fcm, reg);
        })
        .then(token => {
          if (!token) {
            return;
          }
          this.registerFCMTokenWithServer(token);
          LocalStorageUtil.setObject('firebase-token', token);
          this.setState({ firebaseToken: token, desktopAlerts: true });
          LocalStorageUtil.updateObject('settings', { desktopAlerts: true });

          // Обрабатываем FCM push:
          // (a) для каналов всегда,
          // (b) для push-сообщений, когда приложение открыто, но не в фокусе.
          if (this.unsubscribeFCMMessage) {
            this.unsubscribeFCMMessage();
          }
          this.unsubscribeFCMMessage = firebaseOnMessage(this.fcm, payload => {
            this.handlePushMessage(payload);
          });
        })
        .catch(err => {
          // Регистрация сервисного воркера или инициализация FCM завершилась ошибкой.
          onError('Ошибка регистрации сервисного воркера или инициализации FCM', err);
          throw err;
        });
    } catch (err) {
      onError('Ошибка регистрации сервисного воркера или инициализации FCM', err);
      return Promise.reject(err);
    }
  }

  // Google's FCM API is idiotic.
  static requestFCMToken(fcm, sw) {
    return firebaseGetToken(fcm, {
      serviceWorkerRegistration: sw,
      vapidKey: FIREBASE_INIT.messagingVapidKey,
    }).then(token => {
      if (token) {
        return token;
      } else if (typeof Notification != 'undefined') {
        // Try to request permissions.
        return Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            return firebaseGetToken(fcm, {
              serviceWorkerRegistration: sw,
              vapidKey: FIREBASE_INIT.messagingVapidKey,
            }).then(token => {
              if (token) {
                return token;
              } else {
                throw new Error('Failed to initialize notifications');
              }
            });
          } else {
            throw new Error('No permission to send notifications: ' + permission);
          }
        });
      }
      throw new Error('Notifications are not supported');
    });
  }

  /**
   * Регистрирует уже полученный FCM token в текущей Tinode-сессии.
   *
   * @param {string} token FCM registration token, полученный от Firebase Messaging.
   * @returns {boolean} True, если SDK отправил token на сервер в этом вызове.
   */
  registerFCMTokenWithServer(token) {
    if (this.state.guestLogin) {
      return false;
    }
    return this.tinode.setDeviceToken(token);
  }

  handleResize() {
    const mobile = document.documentElement.clientWidth <= MEDIA_BREAKPOINT;
    this.setState({
      viewportWidth: document.documentElement.clientWidth,
      viewportHeight: document.documentElement.clientHeight,
    });
    if (this.state.displayMobile != mobile) {
      this.setState({ displayMobile: mobile });
    }
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      this.infoNavigator(null);
    }
  }

  // Check if a newer version of TinodeWeb app is available at the server.
  checkForAppUpdate(reg) {
    let updateShown = false;
    const showUpdateAvailable = _ => {
      // Один и тот же worker может пройти несколько состояний подряд,
      // но уведомление пользователю нужно показать только один раз.
      if (updateShown) {
        return;
      }
      updateShown = true;
      const msg = (
        <>
          <FormattedMessage
            id="update_available"
            defaultMessage="Update available."
            description="Message shown when an app update is available."
          />{' '}
          <a href="">
            <FormattedMessage
              id="reload_update"
              defaultMessage="Reload"
              description="Call to action to reload application when update is available."
            />
          </a>
          .
        </>
      );
      this.handleError(msg, 'info');
    };

    const watchInstallingWorker = installingWorker => {
      if (!installingWorker) {
        return;
      }
      const onStateChange = _ => {
        // Из-за self.skipWaiting() новый worker может быстро перескочить installed
        // и оказаться в activating/activated до обработки события.
        if (
          ['installed', 'activating', 'activated'].includes(installingWorker.state) &&
          navigator.serviceWorker.controller
        ) {
          showUpdateAvailable();
        }
      };
      installingWorker.onstatechange = onStateChange;
      onStateChange();
    };

    // Если новый worker уже ждёт активации, updatefound больше не придёт.
    if (reg.waiting && navigator.serviceWorker.controller) {
      showUpdateAvailable();
    }

    // Закрывает случай, когда register() вернул регистрацию уже с installing worker.
    watchInstallingWorker(reg.installing);

    reg.onupdatefound = _ => {
      // Обычный сценарий: браузер нашёл новую версию service worker.
      watchInstallingWorker(reg.installing);
    };
  }

  // Handle for hash navigation (hashchange) event: update state.
  handleHashRoute() {
    const hash = HashNavigation.parseUrlHash(window.location.hash);
    // Start with panel parameters.
    const newState = {
      infoPanel: hash.params.info,
      newTopicTabSelected: hash.params.tab,
    };

    if (hash.path && hash.path.length > 0) {
      // Left-side panel selector.
      if (
        [
          'acc_general',
          'archive',
          'blocked',
          'contacts',
          'crop',
          'edit',
          'general',
          'login',
          'newtpk',
          'notif',
          'security',
          'settings',
          'support',
          'wallpapers',
          '',
        ].includes(hash.path[0])
      ) {
        newState.sidePanelSelected = hash.path[0];
      } else {
        console.warn('Unknown sidepanel view', hash.path[0]);
      }

      // Topic for MessagesView selector.
      let topicName = hash.path[1] || null;
      if (topicName != this.state.topicSelected) {
        if (!Tinode.topicType(topicName)) {
          // Замените отсутствующую или недействительную тему первым доступным чатом.
          topicName = this.firstAvailableTopic();
        } else {
          // Topic valid: show messages view on mobile.
          newState.mobilePanel = 'topic-view';
        }
        Object.assign(newState, {
          topicSelected: topicName,
          topicSelectedOnline: this.tinode.isTopicOnline(topicName),
          topicSelectedAcs: this.tinode.getTopicAccessMode(topicName),
        });
        if (topicName) {
          newState.mobilePanel = 'topic-view';
        }
      }
    } else {
      // Empty hashpath
      const topicName = this.firstAvailableTopic();
      Object.assign(newState, {
        sidePanelSelected: '',
        topicSelected: topicName,
        topicSelectedOnline: this.tinode.isTopicOnline(topicName),
        topicSelectedAcs: this.tinode.getTopicAccessMode(topicName),
      });
      if (topicName) {
        newState.mobilePanel = 'topic-view';
      }
    }

    this.setState(newState);
  }

  handleOnline(online) {
    if (online) {
      this.handleError();
      clearInterval(this.reconnectCountdown);
      this.tinode.reconnect();
    } else {
      this.handleError(this.props.intl.formatMessage(messages.no_connection), 'warn');
    }
    this.setState({ liveConnection: online });
  }

  /**
   * Готовит страницу к помещению в BFCache: отключает ожидаемый disconnect
   * от UI, закрывает старый WebSocket и сбрасывает состояние подключения.
   *
   * @param {PageTransitionEvent} event Событие ухода страницы.
   */
  handlePageHide(event) {
    if (!event.persisted || !this.tinode) {
      return;
    }

    this.bfcacheGuard.suspend();
    clearInterval(this.reconnectCountdown);
    clearInterval(this.ssoSessionTicker);

    this.hiWaiters.splice(0);

    this.tinode.onDisconnect = undefined;
    if (this.tinode.isConnected()) {
      this.tinode.disconnect();
    }

    this.setState({
      connected: false,
      ready: false,
      topicSelectedOnline: false,
      applicationVisible: false,
      serverVersion: 'no connection',
    });
  }

  /**
   * Восстанавливает Tinode-сессию после возврата страницы из BFCache.
   *
   * @param {PageTransitionEvent} event Событие возврата страницы.
   */
  handlePageShow(event) {
    if (!event.persisted || !this.bfcacheGuard.isSuspended() || !this.tinode) {
      return;
    }

    this.bfcacheGuard.resume();
    this.tinode.onDisconnect = this.handleDisconnect;
    this.handleError();
    const topicSelected = this.state.topicSelected;
    this.setState(
      {
        connected: false,
        ready: false,
        topicSelectedOnline: false,
        applicationVisible: !document.hidden,
        authRestoring: true,
        loginDisabled: true,
        loadSpinnerVisible: true,
      },
      () => this.startSilentSSOLogin(topicSelected)
    );
  }

  handleVisibilityEvent() {
    this.setState({ applicationVisible: !document.hidden });
  }

  static stateForError(err, level, action, actionText) {
    return {
      errorText: err,
      errorLevel: level,
      errorAction: action,
      errorActionText: actionText,
      callShouldStart: false,
    };
  }

  /**
   * Показывает ошибку пользователю, кроме ожидаемого disconnect от BFCache.
   *
   * @param {Error|string} err Ошибка или текст ошибки.
   * @param {string} level Уровень сообщения для панели ошибок.
   * @param {Function} action Действие, доступное из панели ошибок.
   * @param {string} actionText Текст действия в панели ошибок.
   */
  handleError(err, level, action, actionText) {
    if (this.bfcacheGuard.isExpectedDisconnect(err)) {
      return;
    }
    this.setState(TinodeWeb.stateForError(err, level, action, actionText));
  }

  // Connection succeeded.
  handleConnected() {
    // Just to be sure.
    clearInterval(this.reconnectCountdown);
    this.handleError();

    const params = this.tinode.getServerInfo();
    this.setState({
      serverVersion: params.ver + ' ' + (params.build ? params.build : 'none'),
    });

    // connectAfterHi ждет именно этот момент: transport уже получил {hi} ack.
    // Здесь только разбуживаем ожидающие login-flow промисы. Новый auth-flow отсюда не стартуем,
    // иначе легко получить двойной login или бесконечный spinner.
    const waiters = this.hiWaiters.splice(0);
    waiters.forEach(waiter => waiter.resolve());
  }

  // transport.connect() завершается при открытии сокета, а не после ответа на {hi}.
  connectAfterHi() {
    if (this.tinode.isConnected() && this.tinode.getServerInfo()) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      this.hiWaiters.push({ resolve, reject });
      if (!this.tinode.isConnected()) {
        this.tinode.connect().catch(err => {
          const waiters = this.hiWaiters.splice(0);
          waiters.forEach(waiter => waiter.reject(err));
        });
      }
    });
  }

  // Called for each auto-reconnect iteration.
  handleAutoreconnectIteration(sec, prom) {
    clearInterval(this.reconnectCountdown);

    if (sec < 0) {
      // Clear error
      this.handleError();
      return;
    }

    if (prom) {
      // Reconnecting now
      prom
        .then(_ => {
          // Reconnected: clear error
          this.handleError();
        })
        .catch(err => {
          this.handleError(err.message, 'err');
        });
      return;
    }

    const { formatMessage } = this.props.intl;
    let count = sec / 1000;
    count = count | count;
    this.reconnectCountdown = setInterval(_ => {
      if (count < -10) {
        // Count could go to negative if computer woke up from sleep.
        clearInterval(this.reconnectCountdown);
        this.tinode.reconnect();
        return;
      }

      const fmtTime = count > 99 ? secondsToTime(count) : count;
      this.handleError(
        formatMessage(messages.reconnect_countdown, { seconds: fmtTime }),
        'warn',
        _ => {
          clearInterval(this.reconnectCountdown);
          this.tinode.reconnect();
        },
        formatMessage(messages.reconnect_now)
      );
      count -= 1;
    }, 1000);
  }

  // Соединение потеряно.
  handleDisconnect(err) {
    if (this.bfcacheGuard.isSuspended() || this.bfcacheGuard.isExpectedDisconnect(err)) {
      return;
    }

    const waiters = this.hiWaiters.splice(0);
    waiters.forEach(waiter => waiter.reject(err || new Error('Disconnected')));

    if (
      this.state.transport === FORCED_TRANSPORT &&
      !this.state.guestLogin &&
      (err?.code === 401 || err?.code === 403)
    ) {
      clearInterval(this.ssoSessionTicker);
      this.clearTinodeAuthToken();
      this.tinode.onDisconnect = undefined;
      this.tinode.disconnect();
      this.tinode.onDisconnect = this.handleDisconnect;
      this.clearSSOIdentity();
      window.location.assign(ssoLoginUrl());
      return;
    }

    this.setState({
      connected: false,
      ready: false,
      topicSelectedOnline: false,
      errorText: err && err.message ? err.message : 'Disconnected',
      errorLevel: err && err.message ? 'err' : 'warn',
      loginDisabled: false,
      contextMenuVisible: false,
      serverVersion: 'no connection',
    });
  }

  /**
   * Тихо восстанавливает SSO или открывает гостя, если SSO-cookie нет.
   *
   * Важно: сохраненная авторизация SDK без живого WebSocket не считается
   * готовой сессией. После BFCache SDK может помнить пользователя, но транспорт
   * уже закрыт, поэтому перед быстрым выходом проверяется и isConnected().
   *
   * @param {string} goToTopic Топик, который нужно открыть после восстановления.
   */
  startSilentSSOLogin(goToTopic) {
    if (this.tinode.isAuthenticated() && this.tinode.isConnected()) {
      this.handleLoginSuccessful(goToTopic);
      return;
    }

    this.setState({
      authRestoring: true,
      autoLogin: false,
      loginDisabled: true,
      loadSpinnerVisible: true,
      sidePanelSelected: '',
    });

    authClient
      .getSSOSession()
      .then(session => {
        if (session.status === SSO_SESSION_STATUS.INVALID || session.status === SSO_SESSION_STATUS.ERROR) {
          window.location.assign(ssoLoginUrl());
          return null;
        }
        if (session.status === SSO_SESSION_STATUS.MISSING) {
          this.clearTinodeAuthToken();
          this.clearSSOIdentity();
          this.startGuestLogin(goToTopic);
          return null;
        }
        if (session.ssoUserId != null) {
          this.setState({ ssoUserId: session.ssoUserId });
        }
        this.clearTinodeAuthToken();
        return this.connectAfterHi().then(_ => loginSSO(this.tinode, SSO_LOGIN_MARKER, SSO_LOGIN_MARKER));
      })
      .then(ctrl => {
        if (!ctrl) {
          return;
        }
        if (ctrl.code === 300 && ctrl.text === 'challenge') {
          window.location.assign(ssoProfileUrl());
          return;
        }
        if (ctrl.code === 401 || ctrl.code === 403) {
          window.location.assign(ssoLoginUrl());
          return;
        }
        if (ctrl.code >= 300) {
          throw new Error(ctrl.text);
        }
        this.handleLoginSuccessful(goToTopic);
      })
      .catch(err => {
        if (this.bfcacheGuard.isExpectedDisconnect(err)) {
          return;
        }
        this.clearTinodeAuthToken();
        this.clearSSOIdentity();
        this.startGuestLogin(goToTopic);
      });
  }

  // Выполняет автоматический вход под общим гостевым аккаунтом.
  startGuestLogin(goToTopic) {
    this.setState({
      guestLogin: true,
      myUserId: null,
      ssoUserId: null,
      sidePanelTitle: null,
      sidePanelAvatar: null,
      myTrustedBadges: [],
      authRestoring: true,
      autoLogin: false,
      loginDisabled: true,
      loadSpinnerVisible: true,
      sidePanelSelected: '',
    });

    this.connectAfterHi()
      .then(_ => loginSSO(this.tinode, GUEST_LOGIN, GUEST_PASSWORD))
      .then(ctrl => {
        if (ctrl.code >= 300) {
          throw new Error(ctrl.text);
        }
        this.handleLoginSuccessful(goToTopic);
      })
      .catch(err => {
        if (this.bfcacheGuard.isExpectedDisconnect(err)) {
          return;
        }
        this.bfcacheGuard.finishResume();
        this.setState({
          guestLogin: false,
          authRestoring: false,
          autoLogin: false,
          loginDisabled: false,
          loadSpinnerVisible: false,
          sidePanelSelected: '',
        });
        this.handleError(err.message, 'err');
      });
  }

  handleLoginSuccessful(goToTopic) {
    this.handleError();

    const currentUserId = this.tinode.getCurrentUserID();

    // On reconnect, requestedTopic is undefined but topicSelected may still be set.
    // Preserve the open topic so it gets restored after re-login.
    goToTopic = goToTopic || this.state.requestedTopic || this.state.topicSelected;
    // Logged in fine, subscribe to 'me' attaching callbacks from the contacts view.
    const me = this.tinode.getMeTopic();
    me.onMetaDesc = this.tnMeMetaDesc;
    me.onContactUpdate = this.tnMeContactUpdate;
    me.onSubsUpdated = this.tnMeSubsUpdated;
    this.setState({
      connected: true,
      myUserId: currentUserId,
      autoLogin: false,
      authRestoring: false,
      loginDisabled: false,
      requestedTopic: undefined,
    });
    this.startSSOSessionWatch();
    if (!this.state.guestLogin && this.state.desktopAlertsEnabled && this.state.desktopAlerts) {
      this.initFCMessaging().catch(_ => {
        // Ошибка уже обработана внутри initFCMessaging.
        // catch нужен только для подавления лишнего логирования отклонённого промиса.
      });
    }
    // Subscribe, fetch topic desc, the list of subscriptions. Messages are not fetched.
    me.subscribe(me.startMetaQuery().withLaterSub().withDesc().withTags().withCred().build())
      .then(_ => {
        this.resetContactList();
        this.navigateToTopicOrFallback(goToTopic);
      })
      .catch(err => {
        if (this.bfcacheGuard.isExpectedDisconnect(err)) {
          return;
        }
        this.bfcacheGuard.finishResume();
        this.tinode.disconnect();
        this.handleError(err.message, 'err');
        HashNavigation.navigateTo('');
      })
      .finally(_ => {
        this.bfcacheGuard.finishResume();
        this.setState({
          authRestoring: false,
          loginDisabled: false,
          loadSpinnerVisible: false,
        });
      });
  }

  // WebSocket не передает cookie после Upgrade, поэтому отдельно проверяем их наличие.
  startSSOSessionWatch() {
    if (this.state.transport === 'lp' && !this.state.guestLogin) return;
    clearInterval(this.ssoSessionTicker);

    this.ssoSessionTicker = setInterval(() => {
      authClient
        .getSSOSession()
        .then(session => {
          // Вкладка была гостем, но пользователь вошёл через SSO в другой вкладке.
          // Браузер не сообщает JS об изменении cookies, поэтому проверяем это polling'ом.
          if (this.state.guestLogin) {
            if (session.shouldReloadGuestSession) {
              clearInterval(this.ssoSessionTicker);
              window.location.reload();
            }
            return;
          }

          if (session.status === SSO_SESSION_STATUS.INVALID || session.status === SSO_SESSION_STATUS.ERROR) {
            clearInterval(this.ssoSessionTicker);
            window.location.assign(ssoLoginUrl());
            return;
          }

          // SSO-cookie пропали: после reload откроемся гостем.
          if (session.status === SSO_SESSION_STATUS.MISSING) {
            clearInterval(this.ssoSessionTicker);
            this.clearTinodeAuthToken();
            this.clearSSOIdentity();
            window.location.reload();
            return;
          }

          if (session.ssoUserId != null && session.ssoUserId !== this.state.ssoUserId) {
            this.setState({ ssoUserId: session.ssoUserId });
          }

          if (session.profileChanged) {
            const me = this.tinode.getMeTopic();
            if (me) {
              me.getMeta(me.startMetaQuery().withDesc().build()).catch(err =>
                console.warn('Failed to refresh own profile', err)
              );
            }
          }
        })
        .catch(_ => {
          if (!this.state.guestLogin) {
            clearInterval(this.ssoSessionTicker);
            this.clearTinodeAuthToken();
            this.clearSSOIdentity();
            window.location.reload();
          }
        });
    }, SSO_SESSION_CHECK_INTERVAL);
  }

  tnMeMetaDesc(desc) {
    if (!desc) {
      return;
    }

    if (desc.public) {
      const publicName = this.state.guestLogin
        ? localizedPublicName(desc.public.fn, this.props.intl.formatMessage, messages.guest_user_name)
        : desc.public.fn;
      this.setState({
        sidePanelTitle: publicName,
        sidePanelAvatar: makeImageUrl(desc.public.photo),
      });
    }

    if (desc.trusted) {
      const badges = [];
      for (const [key, val] of Object.entries(desc.trusted)) {
        if (val) {
          badges.push(key);
        }
      }
      this.setState({
        myTrustedBadges: badges,
      });
    }

    if (desc.acs) {
      this.setState({
        incognitoMode: !desc.acs.isPresencer(),
      });
    }

    if (TinodeWeb.checkIfPinsUpdated(desc, this.state.chatList)) {
      this.resetContactList();
    }
  }

  // Reactions to updates to the contact list.
  tnMeContactUpdate(what, cont) {
    if (what == 'on' || what == 'off') {
      this.resetContactList();
      if (this.state.topicSelected == cont.topic) {
        this.setState({ topicSelectedOnline: what == 'on' });
      }
    } else if (what == 'read') {
      this.resetContactList();
    } else if (what == 'msg' && cont) {
      // Check if the topic is archived. If so, don't play a sound.
      const topic = this.tinode.getTopic(cont.topic);
      const archived = topic && topic.isArchived();

      // New message received. Maybe the message is from the current user, then unread is 0.
      if (cont.unread > 0 && this.state.messageSounds && !archived) {
        // Skip update if the topic is currently open, otherwise the badge will annoyingly flash.
        if (document.hidden || this.state.topicSelected != cont.topic) {
          POP_SOUND.play().catch(_ => {
            // play() throws if the user did not click the app first: https://goo.gl/xX8pDD.
          });
        }
      }
      // Reorder contact list to use possibly updated 'touched'.
      this.resetContactList();
    } else if (what == 'recv') {
      // Explicitly ignoring "recv" -- it causes no visible updates to contact list.
    } else if (what == 'gone' || what == 'unsub') {
      // Topic deleted or user unsubscribed. Remove topic from view.
      // If the currently selected topic is gone, clear the selection.
      if (this.state.topicSelected == cont.topic) {
        this.handleTopicSelected(null);
      }
      // Redraw without the deleted topic.
      this.resetContactList();
    } else if (what == 'acs') {
      // Permissions changed. If it's for the currently selected topic,
      // update the views.
      if (this.state.topicSelected == cont.topic) {
        this.setState({ topicSelectedAcs: cont.acs });
      }
    } else if (what == 'del') {
      // TODO: messages deleted (hard or soft) -- update pill counter.
    } else if (what == 'upd' || what == 'call') {
      // upd, call - handled by the SDK. Explicitly ignoring here.
    } else {
      // TODO(gene): handle other types of notifications:
      // * ua -- user agent changes (maybe display a pictogram for mobile/desktop).
      console.info('Unsupported (yet) presence update:', what, 'in', (cont || {}).topic);
    }
  }

  tnMeSubsUpdated(unused) {
    this.resetContactList();
  }

  // Merge search results and contact list to create a single flat
  // list of known contacts for GroupManager to use.
  static prepareSearchableContacts(chatList, foundContacts) {
    const merged = {};

    // For chatList topics merge only p2p topics and convert them to the
    // same format as foundContacts.
    for (const c of chatList) {
      if (Tinode.isP2PTopicName(c.topic)) {
        merged[c.topic] = {
          user: c.topic,
          updated: c.updated,
          public: c.public,
          private: c.private,
          acs: c.acs,
        };
      }
    }

    // Add all foundCountacts if they have not been added already.
    for (const c of foundContacts) {
      if (!merged[c.user]) {
        merged[c.user] = c;
      }
    }

    return Object.values(merged);
  }

  static firstAvailableTopic(chatList, excludeTopic) {
    return (
      (
        chatList.find(
          c => c.topic != excludeTopic && !(c.acs && !c.acs.isJoiner()) && !(c.private && c.private.arch)
        ) || {}
      ).topic || null
    );
  }

  firstAvailableTopic(excludeTopic) {
    return TinodeWeb.firstAvailableTopic(this.state.chatList || [], excludeTopic);
  }

  navigateToTopicOrFallback(topicName, excludeTopic) {
    const nextTopic = topicName || this.firstAvailableTopic(excludeTopic);
    if (nextTopic) {
      HashNavigation.navigateTo(HashNavigation.setUrlTopic('', nextTopic));
      return true;
    }
    return false;
  }

  shouldShowSidePanel() {
    if (this.state.myUserId) {
      return ACCOUNT_SETTINGS_PANELS.includes(this.state.sidePanelSelected);
    }
    return !this.state.authRestoring && AUTH_PANELS.includes(this.state.sidePanelSelected);
  }

  resetContactList() {
    const newState = {
      chatList: [],
    };

    if (!this.state.ready) {
      newState.ready = true;
    }

    const me = this.tinode.getMeTopic();
    me.contacts(c => {
      if (!c.topic && !c.user) {
        // Contacts expect c.topic to be set.
        c.topic = c.name;
      }

      newState.chatList.push(c);
      if (this.state.topicSelected == c.topic) {
        newState.topicSelectedOnline = c.online;
        newState.topicSelectedAcs = c.acs;
      }
    });

    const past = new Date(0);
    newState.chatList.sort((a, b) => {
      // Pinned topics take precedence.
      const pins = me.pinnedTopicRank(b.topic) - me.pinnedTopicRank(a.topic);
      // Then sort by last touched time.
      return pins != 0 ? pins : (b.touched || past).getTime() - (a.touched || past).getTime();
    });

    // После входа автоматически открываем первый доступный неархивный чат.
    const firstTopic = TinodeWeb.firstAvailableTopic(newState.chatList);
    if (!this.state.topicSelected && !this.state.requestedTopic && firstTopic) {
      newState.topicSelected = firstTopic;
      newState.topicSelectedOnline = this.tinode.isTopicOnline(firstTopic);
      newState.topicSelectedAcs = this.tinode.getTopicAccessMode(firstTopic);
      newState.mobilePanel = 'topic-view';
      this.navigateToTopicOrFallback(firstTopic);
    }

    // Merge search results and chat list.
    newState.searchableContacts = TinodeWeb.prepareSearchableContacts(newState.chatList, this.state.searchResults);
    this.setState(newState);
  }

  static checkIfPinsUpdated(me, chatList) {
    for (let i = 0; i < chatList.length; i++) {
      const c = chatList[i];
      if (!!me.pinnedTopicRank(c.topic) != c.pinned) {
        return true;
      }
    }
    return false;
  }

  /* Fnd topic: find contacts by tokens */
  tnInitFind() {
    const fnd = this.tinode.getFndTopic();
    fnd.onSubsUpdated = this.tnFndSubsUpdated;
    if (fnd.isSubscribed()) {
      this.tnFndSubsUpdated();
    } else {
      fnd.subscribe(fnd.startMetaQuery().withSub().build()).catch(err => {
        this.handleError(err.message, 'err');
      });
    }
  }

  tnFndSubsUpdated() {
    const foundContacts = [];
    // Don't attempt to create P2P topics which already exist. Server will reject the duplicates.
    this.tinode.getFndTopic().contacts(s => {
      foundContacts.push(s);
    });
    this.setState({
      searchResults: foundContacts,
      searchableContacts: TinodeWeb.prepareSearchableContacts(this.state.chatList, foundContacts),
    });
  }

  /** Called when the user enters a contact into the contact search field in the NewAccount panel
   @param query {Array} is an array of contacts to search for
   */
  handleSearchContacts(query) {
    const fnd = this.tinode.getFndTopic();
    fnd
      .setMeta({ desc: { public: query } })
      .then(_ => fnd.getMeta(fnd.startMetaQuery().withSub().build()))
      .catch(err => this.handleError(err.message, 'err'));
  }

  // User clicked on a topic in the side panel or deleted a topic.
  handleTopicSelected(topicName) {
    // Clear newTopicParams after use.
    if (this.state.newTopicParams && this.state.newTopicParams._topicName != topicName) {
      this.setState({
        newTopicParams: null,
      });
    }

    if (topicName) {
      this.setState({
        errorText: '',
        errorLevel: null,
        mobilePanel: 'topic-view',
        infoPanel: undefined,
      });
      // Different topic selected.
      if (this.state.topicSelected != topicName) {
        this.setState({
          topicSelectedOnline: this.tinode.isTopicOnline(topicName),
          topicSelectedAcs: this.tinode.getTopicAccessMode(topicName),
        });
        HashNavigation.navigateTo(HashNavigation.setUrlTopic('', topicName));
      }
    } else {
      // Currently selected contact was deleted: switch to another available chat.
      if (!this.navigateToTopicOrFallback(null, this.state.topicSelected)) {
        this.setState({
          topicSelected: null,
          errorText: '',
          errorLevel: null,
          mobilePanel: 'sidepanel',
          topicSelectedOnline: false,
          topicSelectedAcs: null,
          infoPanel: undefined,
        });
      }
    }
  }

  // In mobile view user requested to show sidepanel
  handleHideMessagesView() {
    this.setState({
      mobilePanel: 'sidepanel',
    });
  }

  // User is sending a message, either plain text or a drafty object, possibly
  // with attachments.
  //  - msg - Drafty message with content
  //  - promise - Promise to be resolved when the upload is completed
  //  - uploadCompletionPromise - for tracking progress
  //  - head - head dictionary to be attached to the message
  handleSendMessage(msg, uploadCompletionPromise, uploader, head) {
    const topic = this.tinode.getTopic(this.state.topicSelected);
    return this.sendMessageToTopic(topic, msg, uploadCompletionPromise, uploader, head);
  }

  sendMessageToTopic(topic, msg, uploadCompletionPromise, uploader, head) {
    msg = topic.createMessage(msg, false);
    // The uploader is used to show progress.
    msg._uploader = uploader;

    if (head) {
      msg.head = Object.assign(msg.head || {}, head);
    }

    const completion = [];
    if (uploadCompletionPromise) {
      completion.push(uploadCompletionPromise);
    }

    if (!topic.isSubscribed()) {
      // Topic is not subscribed yet. Subscribe.
      const subscribePromise = topic.subscribe().then(_ => {
        // If there are unsent messages (except video call messages),
        // try sending them now. Unsent video call messages will be dropped.
        let calls = [];
        topic.queuedMessages(pub => {
          if (pub._sending || pub.seq == msg.seq) {
            return;
          }
          if (pub.head && pub.head.webrtc) {
            // Filter out unsent video call messages.
            calls.push(pub.seq);
            return;
          }
          if (topic.isSubscribed()) {
            topic.publishMessage(pub);
          }
        });
        if (calls.length > 0) {
          topic.delMessagesList(calls, true);
        }
      });
      completion.push(subscribePromise);
    }

    // TODO: check if return is required.
    return topic
      .publishDraft(msg, Promise.all(completion))
      .then(ctrl => {
        if (topic.isArchived()) {
          topic.archive(false);
        }
        return ctrl;
      })
      .catch(err => this.handleError(err.message, 'err'));
  }

  handleNewChatInvitation(topicName, action) {
    const topic = this.tinode.getTopic(topicName);
    let response = null;
    switch (action) {
      case 'accept':
        // Accept given permissions.
        const mode = topic.getAccessMode().getGiven();
        response = topic.setMeta({ sub: { mode: mode } });
        if (topic.isP2PType()) {
          // For P2P topics change 'given' permission of the peer too.
          // In p2p topics the other user has the same name as the topic.
          response = response.then(_ => topic.setMeta({ sub: { user: topicName, mode: mode } }));
        }
        break;
      case 'delete':
        // Ignore invitation by deleting it.
        response = topic.delTopic(true);
        break;
      case 'block':
        // Ban the topic making futher invites impossible.
        // Just self-ban.
        const am = topic.getAccessMode().updateWant('-JP').getWant();
        response = topic.setMeta({ sub: { mode: am } }).then(_ => this.handleTopicSelected(null));
        break;
      default:
        console.warn('Unknown invitation action', '"' + action + '""');
    }

    if (response != null) {
      response.catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleToggleIncognitoMode(on) {
    // Make state undefined.
    this.setState({ incognitoMode: null });

    const me = this.tinode.getMeTopic();
    const am = me
      .getAccessMode()
      .updateWant(on ? '-P' : '+P')
      .getWant();
    me.setMeta({ sub: { mode: am } }).catch(err => {
      // Request failed, keep existing state.
      this.setState({ incognitoMode: !on });
      this.handleError(err.message, 'err');
    });
  }

  // Manual change of color scheme: 'dark', 'light', 'auto'.
  handleChangeColorSchema(schema) {
    this.setState({ colorSchema: schema });
    LocalStorageUtil.updateObject('settings', { colorSchema: schema });
    document.documentElement.style.colorScheme = schema == 'auto' ? 'light dark' : schema;
    this.applyColorSchema(schema, this.state.systemColorSchema, this.state.wallpaperSize);
  }

  // System color scheme has changed.
  handleColorSchemeChange(event) {
    const systemSchema = event.matches ? 'dark' : 'light';
    this.setState({ systemColorSchema: systemSchema });
    this.applyColorSchema(this.state.colorSchema, systemSchema, this.state.wallpaperSize);
  }

  applyColorSchema(schema, systemSchema, size) {
    const effectiveSchema = schema == 'auto' ? systemSchema : schema;
    document.documentElement.style.setProperty('--wallpaper-invert', effectiveSchema == 'dark' && size ? '1' : '0');
    document.documentElement.style.setProperty(
      '--wallpaper-brightness',
      effectiveSchema == 'dark' && !size ? '0.5' : '1'
    );
  }

  handleSelectWallpapers() {
    this.handleError();
    HashNavigation.navigateTo(HashNavigation.setUrlSidePanel(window.location.hash, 'wallpapers'));
  }

  applyWallpaperSettings(wallpaper, size, blur, effectiveSchema) {
    document.documentElement.style.setProperty('--wallpaper-url', `url('${wallpaper}')`);
    document.documentElement.style.setProperty('--wallpaper-repeat', size ? 'repeat' : 'no-repeat');
    document.documentElement.style.setProperty('--wallpaper-blur', size ? '0px' : `${blur}px`);
    document.documentElement.style.setProperty('--wallpaper-size', size ? `${size}px` : 'cover');
    document.documentElement.style.setProperty('--wallpaper-position', size ? 'unset' : 'center');
    document.documentElement.style.setProperty('--wallpaper-invert', effectiveSchema == 'dark' && size ? '1' : '0');
    document.documentElement.style.setProperty(
      '--wallpaper-brightness',
      effectiveSchema == 'dark' && !size ? '0.5' : '1'
    );
  }

  handleWallpaperSelected(wallpaper, size, blur) {
    this.handleError();
    LocalStorageUtil.updateObject('settings', { wallpaper: wallpaper, wallpaperSize: size, wallpaperBlur: blur });
    this.setState({ wallpaper: wallpaper, wallpaperSize: size, wallpaperBlur: blur });
    this.applyWallpaperSettings(
      wallpaper,
      size,
      blur,
      this.state.colorSchema == 'auto' ? this.state.systemColorSchema : this.state.colorSchema
    );
  }

  handleChangeTextSize(size) {
    this.setState({ textSize: size | 0 });
    LocalStorageUtil.updateObject('settings', { textSize: size });
    document.documentElement.style.setProperty('--message-text-size', `${size}pt`);
  }

  handleSendOnEnter(option) {
    this.setState({ sendOnEnter: option });
    LocalStorageUtil.updateObject('settings', { sendOnEnter: option });
  }

  isAdminSSOUser() {
    return this.state.ssoUserId === ADMIN_SSO_USER_ID;
  }

  handleUpdateAccountTagsRequest(_, tags) {
    this.tinode
      .getMeTopic()
      .setMeta({ tags: tags })
      .catch(err => this.handleError(err.message, 'err'));
  }

  // User chose Settings menu item.
  handleSettings() {
    this.handleError();

    this.setState({ mobilePanel: 'sidepanel' });
    HashNavigation.navigateTo(
      HashNavigation.setUrlSidePanel(window.location.hash, this.state.myUserId ? 'edit' : 'settings')
    );
  }

  // User updated global parameters.
  handleGlobalSettings(settings) {
    const serverAddress = settings.serverAddress || this.state.serverAddress;
    const transport = FORCED_TRANSPORT;
    const secureConnection =
      settings.secureConnection === undefined ? this.state.secureConnection : settings.secureConnection;
    if (this.tinode) {
      this.tinode.clearStorage();
      this.tinode.onDisconnect = undefined;
      this.tinode.disconnect();
    }
    this.tinode = TinodeWeb.tnSetup(
      serverAddress,
      secureConnection,
      transport,
      this.props.intl.locale,
      LocalStorageUtil.getObject('keep-logged-in')
    );
    this.tinode.onConnect = this.handleConnected;
    this.tinode.onDisconnect = this.handleDisconnect;
    this.tinode.onAutoreconnectIteration = this.handleAutoreconnectIteration;
    this.tinode.onInfoMessage = this.handleInfoMessage;
    this.tinode.onDataMessage = this.handleDataMessage;

    this.setState({
      serverAddress: serverAddress,
      transport: transport,
      secureConnection: secureConnection,
    });
    LocalStorageUtil.setObject('settings', {
      serverAddress: serverAddress,
      transport: transport,
      secureConnection: secureConnection,
    });

    HashNavigation.navigateTo(
      HashNavigation.setUrlSidePanel(window.location.hash, this.state.myUserId ? '' : 'settings')
    );
  }

  // User chose 'Archived chats'.
  handleShowArchive() {
    HashNavigation.navigateTo(
      HashNavigation.setUrlSidePanel(window.location.hash, this.state.myUserId ? 'archive' : '')
    );
  }

  // User viewes 'Blocked chats'.
  handleShowBlocked() {
    HashNavigation.navigateTo(
      HashNavigation.setUrlSidePanel(window.location.hash, this.state.myUserId ? 'blocked' : '')
    );
  }

  toggleFCMToken(enabled) {
    if (this.state.guestLogin) {
      return;
    }
    if (enabled) {
      this.setState({ desktopAlerts: null });
      if (!this.state.firebaseToken) {
        this.initFCMessaging().catch(_ => {
          // Ошибка уже обработана внутри initFCMessaging.
        });
      } else {
        this.registerFCMTokenWithServer(this.state.firebaseToken);
        this.setState({ desktopAlerts: true });
        LocalStorageUtil.updateObject('settings', { desktopAlerts: true });
      }
    } else if (this.state.firebaseToken && this.fcm) {
      firebaseDelToken(this.fcm)
        .catch(err => {
          console.error('Unable to delete token.', err);
        })
        .finally(_ => {
          LocalStorageUtil.updateObject('settings', { desktopAlerts: false });
          localStorage.removeItem('firebase-token');
          this.setState({ desktopAlerts: false, firebaseToken: null });
          // Inform the server that the token was deleted.
          this.tinode.setDeviceToken(null);
        });
    } else {
      this.setState({ desktopAlerts: false, firebaseToken: null });
      LocalStorageUtil.updateObject('settings', { desktopAlerts: false });
    }
  }

  handleToggleMessageSounds(enabled) {
    this.setState({ messageSounds: enabled });
    LocalStorageUtil.updateObject('settings', {
      messageSoundsOff: !enabled,
    });
  }

  // User clicked Cancel button in Setting or Sign Up panel.
  handleSidepanelCancel() {
    const parsed = HashNavigation.parseUrlHash(window.location.hash);
    let path = '';
    let mobilePanel = null;
    if (['security', 'support', 'general', 'notif', 'acc_general'].includes(parsed.path[0])) {
      path = 'edit';
    } else if ('crop' == parsed.path[0]) {
      path = 'general';
    } else if ('blocked' == parsed.path[0]) {
      path = 'security';
    } else if ('wallpapers' == parsed.path[0]) {
      path = 'acc_general';
    } else if ('settings' == parsed.path[0]) {
      path = '';
    } else if (this.state.myUserId) {
      // Скрываем панель
      mobilePanel = 'topic-view';
    }
    if (!mobilePanel && path) {
      // Если есть путь к странице боковой панели - показываем
      mobilePanel = 'sidepanel';
    }
    parsed.path[0] = path;
    if (parsed.params) {
      delete parsed.params.code;
      delete parsed.params.method;
      delete parsed.params.tab;
      delete parsed.params.scheme;
      delete parsed.params.token;
    }
    const sidePanelSelected = path == 'login' ? '' : path;
    this.setState(
      {
        errorText: '',
        errorLevel: null,
        mobilePanel: mobilePanel,
        sidePanelSelected: sidePanelSelected,
      },
      _ => {
        HashNavigation.navigateTo(HashNavigation.composeUrlHash(parsed.path, parsed.params));
      }
    );
  }

  // Sidepanel navigator. No need to bind to 'this'.
  basicNavigator(hash) {
    HashNavigation.navigateTo(HashNavigation.setUrlSidePanel(window.location.hash, hash));
  }

  // Topic info navigator. No need to bind to 'this'.
  infoNavigator(hash) {
    HashNavigation.navigateTo(HashNavigation.setUrlInfoPanel(window.location.hash, hash));
  }

  // Request to start a topic, new or selected from search results, or "by ID".
  handleStartTopicRequest(topicName, newTopicParams, isChannel) {
    // Check if topic is indeed new. If not, launch it.
    if (topicName && this.tinode.isTopicCached(topicName)) {
      this.handleTopicSelected(topicName);
      return;
    }

    const params = {};
    if (Tinode.isP2PTopicName(topicName)) {
      // Because we are initiating the subscription, set 'want' to all permissions.
      params.sub = { mode: DEFAULT_P2P_ACCESS_MODE };
      // Give the other user all permissions too.
      params.desc = { defacs: { auth: DEFAULT_P2P_ACCESS_MODE } };
    } else {
      topicName = topicName || this.tinode.newGroupTopicName(isChannel);
      if (newTopicParams) {
        params.desc = { public: newTopicParams.public, private: { comment: newTopicParams.private } };
        params.tags = newTopicParams.tags;
      }
    }
    params._topicName = topicName;
    this.setState({ newTopicParams: params }, _ => {
      this.handleTopicSelected(topicName);
    });
  }

  // New topic was created, here is the new topic name.
  handleNewTopicCreated(oldName, newName) {
    let nextState = {};
    if (this.state.callShouldStart) {
      nextState = { callState: CALL_STATE_IN_PROGRESS, callShouldStart: false };
    }
    if (this.state.topicSelected == oldName && oldName != newName) {
      // If the current URl contains the old topic name, replace it with new.
      // Update the name of the selected topic first so the navigator doen't clear
      // the state.
      nextState.topicSelected = newName;
    }
    this.setState(nextState, _ => {
      HashNavigation.navigateTo(HashNavigation.setUrlTopic('', newName));
    });
  }

  handleTopicUpdateRequest(topicName, pub, priv, defacs) {
    this.handleError();

    const topic = this.tinode.getTopic(topicName);
    if (topic) {
      const params = {};
      let attachments;
      if (pub) {
        if (pub.photo) {
          if (pub.photo.ref && pub.photo.ref != Tinode.DEL_CHAR) {
            attachments = [pub.photo.ref];
          } else if (!pub.photo.data || pub.photo.data == Tinode.DEL_CHAR) {
            pub.photo = Tinode.DEL_CHAR;
          }
        }
        params.public = pub;
      }

      if (typeof priv == 'string') {
        params.private = priv === Tinode.DEL_CHAR ? Tinode.DEL_CHAR : { comment: priv };
      }
      if (defacs) {
        params.defacs = defacs;
      }
      topic.setMeta({ desc: params, attachments: attachments }).catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleUnarchive(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (topic) {
      topic.archive(false).catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleChangePermissions(topicName, mode, uid) {
    const topic = this.tinode.getTopic(topicName);
    if (topic) {
      const am = topic.getAccessMode();
      if (uid) {
        am.updateGiven(mode);
        mode = am.getGiven();
      } else {
        am.updateWant(mode);
        mode = am.getWant();
      }
      topic.setMeta({ sub: { user: uid, mode: mode } }).catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleTagsUpdateRequest(topicName, tags) {
    const topic = this.tinode.getTopic(topicName);
    if (topic) {
      topic.setMeta({ tags: tags }).catch(err => this.handleError(err.message, 'err'));
    }
  }

  handleLogout() {
    updateFavicon(0);
    clearInterval(this.ssoSessionTicker);

    // SSO-сессия завершается через /auth/logout. Настройки интерфейса сохраняем,
    // но push-token отвязываем от текущего пользователя и удаляем из Firebase.
    localStorage.removeItem('firebase-token');
    const firebaseToken = this.state.firebaseToken;
    if (firebaseToken && this.tinode) {
      this.tinode.setDeviceToken(null);
    }

    // Возвращаем визуальные настройки к значениям по умолчанию.
    document.documentElement.style.colorScheme = DEFAULT_COLOR_SCHEME == 'auto' ? 'light dark' : DEFAULT_COLOR_SCHEME;
    document.documentElement.style.setProperty('--message-text-size', `${DEFAULT_TEXT_SIZE}pt`);

    clearInterval(this.reconnectCountdown);

    let cleared = Promise.resolve();
    if (firebaseToken) {
      const fcmDelete = this.fcm
        ? firebaseDelToken(this.fcm).catch(err => {
            console.error('Unable to delete token.', err);
          })
        : Promise.resolve();

      // setDeviceToken(null) отправляет {hi} без подтверждения; даем ему короткое окно до disconnect.
      cleared = Promise.all([fcmDelete, new Promise(resolve => setTimeout(resolve, 300))]);
    }

    if (this.tinode) {
      cleared = cleared.finally(_ => {
        this.tinode.onDisconnect = undefined;
        this.tinode.disconnect();
      });
    }
    cleared.then(_ => window.location.assign(ssoLogoutUrl()));
  }

  handleDeleteAccount() {
    this.tinode.delCurrentUser(true).then(_ => {
      this.handleLogout();
    });
  }

  handleDeleteTopicRequest(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    // Request to hard-delete topic.
    topic
      .delTopic(true)
      .then(_ => {
        this.navigateToTopicOrFallback(null, topicName);
      })
      .catch(err => {
        this.handleError(err.message, 'err');
      });
  }

  handleDeleteMessagesRequest(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    // Request hard-delete all messages.
    topic.delMessagesAll(true).catch(err => this.handleError(err.message, 'err'));
  }

  // Отправляет гостя прямо в SSO, минуя промежуточную форму выбора.
  handleGuestSignInRequest() {
    window.location.assign(ssoLoginUrl());
  }

  handleLeaveUnsubRequest(topicName) {
    // Гость не должен покидать общий чат на сервере через UI.
    if (this.state.guestLogin) {
      return;
    }

    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    topic
      .leave(true)
      .then(_ => {
        this.navigateToTopicOrFallback(null, topicName);
      })
      .catch(err => {
        this.handleError(err.message, 'err');
      });
  }

  handleBlockTopicRequest(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    topic
      .updateMode(null, '-JP')
      .then(_ => {
        this.navigateToTopicOrFallback(null, topicName);
      })
      .catch(err => this.handleError(err.message, 'err'));
  }

  handleReportTopic(topicName) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    // Publish spam report.
    this.tinode.report('report', topicName);

    // Remove J and P permissions.
    topic
      .updateMode(null, '-JP')
      .then(_ => {
        this.navigateToTopicOrFallback(null, topicName);
      })
      .catch(err => this.handleError(err.message, 'err'));
  }

  handleShowContextMenu(params, menuItems) {
    this.setState({
      contextMenuVisible: true,
      contextMenuClickAt: { x: params.x, y: params.y },
      contextMenuParams: params,
      contextMenuItems: menuItems || this.defaultTopicContextMenu(params.topicName),
      contextMenuBounds: this.selfRef.current.getBoundingClientRect(),
    });
  }

  defaultTopicContextMenu(topicName) {
    const topic = this.tinode.getTopic(topicName);

    if (topic._deleted) {
      return ['topic_delete'];
    }

    const me = this.tinode.getMeTopic();
    let muted = false,
      blocked = false,
      self_blocked = false,
      subscribed = false,
      deleter = false,
      archived = false,
      pinned = false,
      webrtc = false,
      writer = false,
      p2p = false,
      self = false;
    if (topic) {
      subscribed = topic.isSubscribed();
      archived = topic.isArchived();
      pinned = me.pinnedTopicRank(topicName) > 0;

      const acs = topic.getAccessMode();
      if (acs) {
        muted = acs.isMuted();
        blocked = !acs.isJoiner();
        self_blocked = !acs.isJoiner('want');
        deleter = acs.isDeleter();
        writer = acs.isWriter();
      }
    }

    webrtc = writer && !!this.tinode.getServerParam('iceServers');
    p2p = Tinode.isP2PTopicName(topicName);
    self = Tinode.isSelfTopicName(topicName);

    return [
      subscribed && this.canUseInfoPanel()
        ? {
            title: this.props.intl.formatMessage(messages.menu_item_info),
            handler: this.handleShowInfoView,
          }
        : null,
      subscribed && p2p && webrtc
        ? {
            title: this.props.intl.formatMessage(messages.menu_item_audio_call),
            handler: this.handleStartAudioCall,
          }
        : null,
      subscribed && p2p && webrtc
        ? {
            title: this.props.intl.formatMessage(messages.menu_item_video_call),
            handler: this.handleStartVideoCall,
          }
        : null,
      subscribed ? 'messages_clear' : null,
      subscribed && deleter && !self ? 'messages_clear_hard' : null,
      self ? null : muted ? (blocked ? null : 'topic_unmute') : 'topic_mute',
      self ? null : self_blocked ? 'topic_unblock' : 'topic_block',
      pinned ? 'topic_unpin' : 'topic_pin',
      archived ? 'topic_restore' : 'topic_archive',
      'topic_delete',
    ];
  }

  handleHideContextMenu() {
    this.setState({
      contextMenuVisible: false,
      contextMenuClickAt: null,
      contextMenuParams: null,
      contextMenuBounds: null,
    });
  }

  handleContextMenuAction(action, promise, params) {
    if (action == 'topic_archive') {
      if (promise && params.topicName && params.topicName == this.state.topicSelected) {
        promise.then(_ => {
          this.handleTopicSelected(null);
        });
      }
    }
  }

  handleShowAlert(title, content, onConfirm, confirmText, onReject, rejectText) {
    this.setState({
      alertVisible: true,
      alertParams: {
        title: title,
        content: content,
        onConfirm: onConfirm,
        confirm: confirmText,
        onReject: onReject,
        reject: rejectText,
      },
    });
  }

  // Проверяем право на доступ к информационной панели.
  canUseInfoPanel() {
    return (
      this.state.ready &&
      this.tinode &&
      this.tinode.isAuthenticated() &&
      this.state.topicSelectedAcs &&
      this.state.topicSelectedAcs.isAdmin()
    );
  }

  handleShowInfoView() {
    if (!this.canUseInfoPanel()) {
      return;
    }
    HashNavigation.navigateTo(HashNavigation.addUrlParam(window.location.hash, 'info', 'info'));
    this.setState({ infoPanel: 'info' });
  }

  handleMemberUpdateRequest(topicName, added, removed) {
    if (!topicName) {
      return;
    }

    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    if (added && added.length > 0) {
      added.forEach(uid => {
        topic.invite(uid, null).catch(err => this.handleError(err.message, 'err'));
      });
    }

    if (removed && removed.length > 0) {
      removed.forEach(uid => {
        topic.delSubscription(uid).catch(err => this.handleError(err.message, 'err'));
      });
    }
  }

  handleStartVideoCall() {
    this.setState({
      callTopic: this.state.topicSelected,
      callState: CALL_STATE_OUTGOING_INITATED,
      callAudioOnly: false,
    });
  }

  handleStartAudioCall() {
    this.setState({
      callTopic: this.state.topicSelected,
      callState: CALL_STATE_OUTGOING_INITATED,
      callAudioOnly: true,
    });
  }

  handleCallInvite(callTopic, callSeq, callState, audioOnly) {
    switch (callState) {
      case CALL_STATE_OUTGOING_INITATED:
        const head = { webrtc: CALL_HEAD_STARTED, aonly: !!audioOnly };
        return this.handleSendMessage(Drafty.videoCall(audioOnly), undefined, undefined, head).then(ctrl => {
          if (ctrl.code < 200 || ctrl.code >= 300 || !ctrl.params || !ctrl.params.seq) {
            this.handleCallClose();
            return ctrl;
          }
          this.setState({ callSeq: ctrl.params['seq'] });
          return ctrl;
        });
      case CALL_STATE_IN_PROGRESS:
        const topic = this.tinode.getTopic(callTopic);
        if (!topic) {
          return;
        }
        // We've accepted the call. Let the other side know.
        topic.videoCall('accept', callSeq);
        break;
    }
  }

  handleCallRinging(callTopic, callSeq) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('ringing', callSeq);
  }

  handleCallHangup(callTopic, callSeq) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('hang-up', callSeq);
  }

  handleCallSendOffer(callTopic, callSeq, sdp) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('offer', callSeq, sdp);
  }

  handleCallIceCandidate(callTopic, callSeq, candidate) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('ice-candidate', callSeq, candidate);
  }
  handleCallSendAnswer(callTopic, callSeq, sdp) {
    const topic = this.tinode.getTopic(callTopic);
    if (!topic) {
      return;
    }

    topic.videoCall('answer', callSeq, sdp);
  }

  handleCallClose() {
    if (this.callTimeoutTimer) {
      clearTimeout(this.callTimeoutTimer);
    }
    this.setState({
      callTopic: undefined,
      callState: CALL_STATE_NONE,
      callAudioOnly: undefined,
    });
  }

  handleCallAccept(topicName, setCallTopic, callSeq) {
    const topic = this.tinode.getTopic(topicName);
    if (!topic) {
      return;
    }
    if (topic.isSubscribed()) {
      this.handleTopicSelected(setCallTopic ? topicName : this.state.callTopic);
      const upd = {
        callState: CALL_STATE_IN_PROGRESS,
      };
      if (setCallTopic) {
        upd.callTopic = topicName;
        upd.callSeq = callSeq;
      }
      this.setState(upd);
    } else {
      // We need to switch and subscribe to callTopic first.
      this.setState(
        {
          callShouldStart: true,
        },
        _ => this.handleTopicSelected(this.state.callTopic)
      );
    }
  }

  handleInfoMessage(info) {
    if (info.what != 'call') {
      return;
    }
    switch (info.event) {
      case 'accept':
        // If another my session has accepted the call.
        if (Tinode.isMeTopicName(info.topic) && this.tinode.isMe(info.from)) {
          this.setState({
            callTopic: null,
            callState: CALL_STATE_NONE,
            callSeq: null,
            callAudioOnly: undefined,
          });
          return;
        }
        if (info.topic == this.state.callTopic) {
          // Update state.
          this.setState({ callState: CALL_STATE_IN_PROGRESS });
        }
        break;
      case 'hang-up':
        // Remote hangup.
        this.handleCallClose();
        break;
    }
  }

  handleDataMessage(data) {
    if (data.head && data.head.webrtc && data.head.webrtc == CALL_HEAD_STARTED && Tinode.isP2PTopicName(data.topic)) {
      // If it's a video call invite message.
      // See if we need to display incoming call UI.
      const topic = this.tinode.getTopic(data.topic);
      if (topic) {
        // Check if a later version of the message exists (which means the call
        // has been either accepted or finished already).
        const msg = topic.latestMsgVersion(data.seq) || data;
        if (msg.head && msg.head.webrtc && msg.head.webrtc == CALL_HEAD_STARTED) {
          // This is a legit new call.
          if (data.from != this.state.myUserId) {
            if (this.state.callState == CALL_STATE_NONE) {
              // Incoming call.
              this.setState({
                callTopic: data.topic,
                callState: CALL_STATE_INCOMING_RECEIVED,
                callSeq: data.seq,
                callAudioOnly: !!msg.head.aonly,
              });
            } else {
              // Another call is either in progress or being established.
              // Reject the incoming call.
              this.handleCallHangup(data.topic, data.seq);
            }
          }
        }
      } else {
        console.warn('Received vc data message from unknown topic', data.topic);
      }
    }
  }

  render() {
    const colorSchema = this.state.colorSchema !== 'auto' ? this.state.colorSchema : this.state.systemColorSchema;
    const selectedTopic =
      this.tinode && this.state.topicSelected ? this.tinode.getTopic(this.state.topicSelected) : null;
    const showSidePanel = this.shouldShowSidePanel();
    const profileLoading =
      this.state.authRestoring ||
      this.state.loadSpinnerVisible ||
      (!!this.state.myUserId && !this.state.sidePanelTitle);
    const chatLoading =
      this.state.authRestoring ||
      this.state.loadSpinnerVisible ||
      (!!this.state.topicSelected && (!selectedTopic || !this.state.topicSelectedAcs));
    const authorizeURL = this.tinode ? url => this.tinode.authorizeURL(url) : null;
    return (
      <div id="app-container" className={styles.appContainer} ref={this.selfRef}>
        {this.state.contextMenuVisible ? (
          <ContextMenu
            tinode={this.tinode}
            bounds={this.state.contextMenuBounds}
            clickAt={this.state.contextMenuClickAt}
            params={this.state.contextMenuParams}
            items={this.state.contextMenuItems}
            hide={this.handleHideContextMenu}
            onShowAlert={this.handleShowAlert}
            onAction={this.handleContextMenuAction}
            onTopicRemoved={topicName => {
              if (topicName == this.state.topicSelected) {
                this.handleTopicSelected(null);
              }
            }}
            onError={this.handleError}
          />
        ) : null}
        {this.state.callTopic && this.state.callState == CALL_STATE_INCOMING_RECEIVED ? (
          <CallIncoming
            tinode={this.tinode}
            topic={this.state.callTopic}
            seq={this.state.callSeq}
            callState={this.state.callState}
            audioOnly={this.state.callAudioOnly}
            onClose={this.handleCallClose}
            onRinging={this.handleCallRinging}
            onAcceptCall={this.handleCallAccept}
            onReject={this.handleCallHangup}
          />
        ) : null}
        {this.state.alertVisible ? (
          <Alert
            title={this.state.alertParams.title}
            content={this.state.alertParams.content}
            onReject={this.state.alertParams.onReject ? _ => this.setState({ alertVisible: false }) : null}
            reject={this.state.alertParams.reject}
            onConfirm={
              this.state.alertParams.onConfirm
                ? _ => {
                    this.setState({ alertVisible: false });
                    this.state.alertParams.onConfirm();
                  }
                : null
            }
            confirm={this.state.alertParams.confirm}
          />
        ) : null}

        <Header
          topic={selectedTopic}
          topicName={this.state.topicSelected}
          authorizeURL={authorizeURL}
          acs={this.state.topicSelectedAcs}
          chatLoading={chatLoading}
          profileLoading={profileLoading}
          user={{
            id: this.state.myUserId,
            name: this.state.sidePanelTitle || '',
            avatar: this.state.sidePanelAvatar || true,
            authorizeURL: authorizeURL,
          }}
          onTopicInfo={this.handleShowInfoView}
          onUserSettings={this.handleSettings}
        />

        {!this.state.displayMobile ? (
          <ErrorPanel
            level={this.state.errorLevel}
            text={this.state.errorText}
            action={this.state.errorAction}
            actionText={this.state.errorActionText}
            onClearError={this.handleError}
          />
        ) : null}

        <div className={styles.appBody}>
          {showSidePanel && (!this.state.displayMobile || this.state.mobilePanel == 'sidepanel') ? (
            // Sidepanel учитывает гостевой режим и состояние восстановления авторизации.
            <SidepanelView
              tinode={this.tinode}
              connected={this.state.connected}
              displayMobile={this.state.displayMobile}
              state={this.state.sidePanelSelected}
              title={this.state.sidePanelTitle}
              avatar={this.state.sidePanelAvatar}
              trustedBadges={this.state.myTrustedBadges}
              myUserId={this.state.myUserId}
              isAdminSSOUser={this.isAdminSSOUser()}
              guestLogin={this.state.guestLogin}
              authRestoring={this.state.authRestoring}
              canGuestSignIn={isRussianLocale(this.props.intl.locale)}
              loadSpinnerVisible={this.state.loadSpinnerVisible}
              errorText={this.state.displayMobile ? this.state.errorText : ''}
              errorLevel={this.state.displayMobile ? this.state.errorLevel : null}
              errorAction={this.state.displayMobile ? this.state.errorAction : undefined}
              errorActionText={this.state.displayMobile ? this.state.errorActionText : null}
              topicSelected={this.state.topicSelected}
              chatList={this.state.chatList}
              transport={this.state.transport}
              messageSounds={this.state.messageSounds}
              desktopAlerts={this.state.desktopAlerts}
              desktopAlertsEnabled={this.state.desktopAlertsEnabled}
              incognitoMode={this.state.incognitoMode}
              serverAddress={this.state.serverAddress}
              secureConnection={this.state.secureConnection}
              serverVersion={this.state.serverVersion}
              textSize={this.state.textSize}
              colorSchema={this.state.colorSchema}
              wallpaper={this.state.wallpaper}
              wallpaperSize={this.state.wallpaperSize}
              wallpaperBlur={this.state.wallpaperBlur}
              systemColorSchema={this.state.systemColorSchema}
              sendOnEnter={this.state.sendOnEnter}
              onGlobalSettings={this.handleGlobalSettings}
              onSettings={this.handleSettings}
              onNavigate={this.basicNavigator}
              onUpdateAccountDesc={this.handleTopicUpdateRequest}
              onUpdateAccountTags={this.handleUpdateAccountTagsRequest}
              onTogglePushNotifications={this.toggleFCMToken}
              onToggleMessageSounds={this.handleToggleMessageSounds}
              onToggleIncognitoMode={this.handleToggleIncognitoMode}
              onChangeColorSchema={this.handleChangeColorSchema}
              onTextSizeChanged={this.handleChangeTextSize}
              onSendOnEnterChanged={this.handleSendOnEnter}
              onSelectWallpapers={this.handleSelectWallpapers}
              onWallpaperSelected={this.handleWallpaperSelected}
              onTopicSelected={this.handleTopicSelected}
              onCreateTopic={this.handleStartTopicRequest}
              onLogout={this.handleLogout}
              onDeleteAccount={this.handleDeleteAccount}
              onShowAlert={this.handleShowAlert}
              onGuestSignIn={this.handleGuestSignInRequest}
              onCancel={this.handleSidepanelCancel}
              onError={this.handleError}
              onShowArchive={this.handleShowArchive}
              onShowBlocked={this.handleShowBlocked}
              onInitFind={this.tnInitFind}
              searchResults={this.state.searchResults}
              onSearchContacts={this.handleSearchContacts}
              showContextMenu={this.handleShowContextMenu}
            />
          ) : null}

          {!this.state.displayMobile || (this.state.mobilePanel == 'topic-view' && !this.state.infoPanel) ? (
            <Suspense
              fallback={
                // Fallback должен занимать место MessagesView, иначе appBody сжимается и интерфейс прыгает.
                <div id="topic-view" className="center-medium-text">
                  <FormattedMessage
                    id="loading_note"
                    defaultMessage="Loading..."
                    description="Message shown when component is loading"
                  />
                </div>
              }
            >
              {/* MessagesView использует гостевой режим для read-only поведения. */}
              <MessagesView
                tinode={this.tinode}
                connected={this.state.connected}
                ready={this.state.ready}
                online={this.state.topicSelectedOnline}
                acs={this.state.topicSelectedAcs}
                displayMobile={this.state.displayMobile}
                viewportWidth={this.state.viewportWidth}
                viewportHeight={this.state.viewportHeight}
                topic={this.state.topicSelected}
                myUserId={this.state.myUserId}
                guestLogin={this.state.guestLogin}
                // User public.fn.
                myUserName={this.state.sidePanelTitle}
                serverVersion={this.state.serverVersion}
                serverAddress={this.state.serverAddress}
                applicationVisible={this.state.applicationVisible}
                colorSchema={colorSchema}
                sendOnEnter={this.state.sendOnEnter}
                wallpaper={this.state.wallpaper}
                callTopic={this.state.callTopic}
                callSeq={this.state.callSeq}
                callState={this.state.callState}
                callAudioOnly={this.state.callAudioOnly}
                onCallHangup={this.handleCallHangup}
                onAcceptCall={this.handleCallAccept}
                onCallInvite={this.handleCallInvite}
                onCallSendOffer={this.handleCallSendOffer}
                onCallIceCandidate={this.handleCallIceCandidate}
                onCallSendAnswer={this.handleCallSendAnswer}
                errorText={this.state.errorText}
                errorLevel={this.state.errorLevel}
                errorAction={this.state.errorAction}
                errorActionText={this.state.errorActionText}
                newTopicParams={this.state.newTopicParams}
                onHideMessagesView={this.handleHideMessagesView}
                canGuestSignIn={isRussianLocale(this.props.intl.locale)}
                onGuestSignIn={this.handleGuestSignInRequest}
                onError={this.handleError}
                onNewTopicCreated={this.handleNewTopicCreated}
                showContextMenu={this.handleShowContextMenu}
                onChangePermissions={this.handleChangePermissions}
                onNewChat={this.handleNewChatInvitation}
                sendMessage={this.handleSendMessage}
                onVideoCallClosed={this.handleCallClose}
              />
            </Suspense>
          ) : null}

          {this.state.infoPanel && this.canUseInfoPanel() ? (
            <Suspense
              fallback={
                // Fallback должен занимать место InfoView, чтобы flex-раскладка не менялась при lazy-load.
                <div id="info-view" className="center-medium-text">
                  <FormattedMessage
                    id="loading_note"
                    defaultMessage="Loading..."
                    description="Message shown when component is loading"
                  />
                </div>
              }
            >
              {/* InfoView скрывает действия, которые гостю недоступны. */}
              <InfoView
                tinode={this.tinode}
                connected={this.state.connected}
                displayMobile={this.state.displayMobile}
                topic={this.state.topicSelected}
                searchableContacts={this.state.searchableContacts}
                myUserId={this.state.myUserId}
                guestLogin={this.state.guestLogin}
                panel={this.state.infoPanel}
                errorText={this.state.errorText}
                errorLevel={this.state.errorLevel}
                errorAction={this.state.errorAction}
                errorActionText={this.state.errorActionText}
                onNavigate={this.infoNavigator}
                onTopicDescUpdateRequest={this.handleTopicUpdateRequest}
                onShowAlert={this.handleShowAlert}
                onChangePermissions={this.handleChangePermissions}
                onMemberUpdateRequest={this.handleMemberUpdateRequest}
                onDeleteTopic={this.handleDeleteTopicRequest}
                onDeleteMessages={this.handleDeleteMessagesRequest}
                onLeaveTopic={this.handleLeaveUnsubRequest}
                onBlockTopic={this.handleBlockTopicRequest}
                onReportTopic={this.handleReportTopic}
                onTopicTagsUpdateRequest={this.handleTagsUpdateRequest}
                onTopicUnArchive={this.handleUnarchive}
                onInitFind={this.tnInitFind}
                onError={this.handleError}
                showContextMenu={this.handleShowContextMenu}
              />
            </Suspense>
          ) : null}
        </div>
      </div>
    );
  }
}

export default injectIntl(TinodeWeb);
